// Cave84 — Auth hook
// Mounts the onAuthStateChanged listener, bootstraps the users/{uid} doc on
// first login, and keeps the Zustand store in sync. Mount this exactly ONCE
// at the app root (in App.tsx). Subsequent components read state from the store.

import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/services/firebase';
import { ensureUserDoc, logGuestLogin } from '@/services/users';
import { consumeRedirectResult } from '@/services/auth';
import { useAuthStore } from '@/stores/auth.store';

export function useAuthBootstrap(): void {
  const setStatus = useAuthStore((s) => s.setStatus);
  const setFirebaseUser = useAuthStore((s) => s.setFirebaseUser);
  const setUserDoc = useAuthStore((s) => s.setUserDoc);
  const reset = useAuthStore((s) => s.reset);

  useEffect(() => {
    let cancelled = false;

    // Pick up any redirect-based sign-in result first (iOS PWA standalone path).
    void consumeRedirectResult();

    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (cancelled) return;

      if (!firebaseUser) {
        reset();
        return;
      }

      setFirebaseUser(firebaseUser);
      setStatus('initializing');
      try {
        const userDoc = await ensureUserDoc(firebaseUser);
        if (cancelled) return;
        setUserDoc(userDoc);
        setStatus('authenticated');
        // Guest (non-owner) sign-ins are logged for the Owner's audit view.
        // Fire-and-forget — must never block reaching 'authenticated'.
        if (userDoc.role !== 'owner') {
          void logGuestLogin(firebaseUser);
        }
      } catch (err) {
        // If the bootstrap fails (e.g. Firestore rules block it because
        // VITE_OWNER_EMAIL is wrong), surface enough info to debug without
        // leaking creds.
        console.error('Cave84: failed to bootstrap user doc.', err);
        setStatus('unauthenticated');
      }
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [setStatus, setFirebaseUser, setUserDoc, reset]);
}
